/** @jsxImportSource @emotion/react */
import * as React from "react";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { useParams } from "react-router-dom";
import { routes } from "../../appRoutingHelpers";
import { SpinnerBlock } from "../common/Spinner";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { useGetVouchers, usePunchCards } from "../../service/api";
import { config } from "../../config";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import { formatMoneyAmount } from "../../util";
import { css } from "@emotion/react";
import { useGetKitchenCategories } from "../kitchen-categories/service/kitchen-categories-api";
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { SmallBadge } from "../../components/SmallBadge";
import { useGetProduct } from "./service/products-api";
import {
  getClassificationDisplayValue,
  ProductClassification,
} from "./models/product";
import { CardInformationRow } from "../../components/page-skeleton/CardInformationRow";
import { Divider } from "../../components/Divider";
import { getCountryInfo } from "../../service/countries-service";
import { UpdateProductSelector } from "./components/UpdateProductSelector";
import {
  ProductVatNotice,
  VAT_RULE_RATE_LABEL,
} from "./components/ProductVatNotice";
import { BaseUploadPreview } from "../../components/BaseUploadPreview";
import { EmptyState } from "../../components/EmptyState";
import { AddPackingItemsSelector } from "./components/AddPackingItemsSelector";
import { ProductPackingItemsTable } from "./components/ProductPackingItemsTable";
import { AdditionGroupsTable } from "./components/AdditionGroupsTable";
import { DeleteProductSelector } from "./components/DeleteProductSelector";
import { useHandleFetchError } from "../../error-handler";

interface Props {}

const tagsContainerStyle = css`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;

const tagStyling = css`
  display: inline-block;
  vertical-align: top;
`;

const additionalCardStyle = css`
  padding-bottom: 24px;
  padding-top: 24px;
`;

const descriptionStyle = css`
  white-space: pre-wrap;
`;

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const cooldownRulesets_STAGING = [
  { label: "None", value: "" },
  { label: "30 seconds", value: "3e61acea-098c-48e7-80d0-94db430808f5" },
  { label: "7 days", value: "89967693-830a-4a13-86bc-ca32b5b70acf" },
];

export const cooldownRulesets_PRODUCTION = [
  { label: "None", value: "" },
  { label: "12 hours", value: "dbf2392d-a952-4757-9b1c-2acc86292af5" },
  { label: "3 days", value: "debe9a34-56b7-11ed-b4f2-0ae1573942a1" },
  { label: "7 days", value: "18e475c8-a640-448c-9ee3-fe6e0ba27711" },
  { label: "Forever", value: "7dc02c88-f4ae-4ce1-b856-abb358095f32" },
];

const getColorsForClassificationBadge = (
  classification: ProductClassification
) => {
  switch (classification) {
    case ProductClassification.Generic:
      return {
        backgroundColor: colors.orangeLight,
        borderColor: colors.orangeMedium,
        textColor: colors.organgeDark,
      };
    case ProductClassification.Addition:
      return {
        backgroundColor: colors.blueLight,
        borderColor: colors.blueInteractive,
        textColor: colors.blueDark,
      };
    case ProductClassification.VoucherOffer:
      return {
        backgroundColor: colors.successLight,
        borderColor: colors.successMedium,
        textColor: colors.successDark,
      };
    case ProductClassification.VoucherStack:
      return {
        backgroundColor: colors.ctaLight,
        borderColor: colors.ctaSecondaryOne,
        textColor: colors.ctaMain,
      };
    // Delivery products - not used as often, so same colors as addition
    default:
      return {
        backgroundColor: colors.blueLight,
        borderColor: colors.blueInteractive,
        textColor: colors.blueDark,
      };
  }
};

const ProductUpsert: React.FC<Props> = (props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const { id } = useParams<{ id: string }>();

  const organization =
    useSelector(authSelectors.selectedOrganizationKeyName) || "";

  const {
    data: product,
    isLoading: isLoadingProduct,
    error: productError,
  } = useGetProduct(organization, id);

  const {
    data: kitchenCategories,
    isLoading: kitchenCategoriesAreLoading,
    error: kitchenCategoriesError,
  } = useGetKitchenCategories(organizationKeyName);

  const {
    data: voucherTypesList,
    isLoading: isLoadingVoucherTypeList,
    error: voucherTypesListError,
  } = useGetVouchers(organization);

  const selectedOrganizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const isEdit = !!id;

  const {
    data: punchCards,
    isLoading: punchCardsLoading,
    error: punchCardsError,
  } = usePunchCards(organization);

  useHandleFetchError(productError);
  useHandleFetchError(kitchenCategoriesError);
  useHandleFetchError(voucherTypesListError);
  useHandleFetchError(punchCardsError);

  if (
    (isEdit && isLoadingProduct) ||
    punchCardsLoading ||
    isLoadingVoucherTypeList ||
    kitchenCategoriesAreLoading ||
    !product
  ) {
    return <SpinnerBlock />;
  }

  const kitchenCategoriesOptions = (kitchenCategories || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((cat) => ({
      label: cat.name,
      value: cat.id,
    }));

  kitchenCategoriesOptions.unshift({
    label: "None",
    value: "",
  });

  return (
    <PageWrapper>
      <GoBackLinkButton
        to={routes.products(selectedOrganizationKeyName)}
        text="Back to all products"
      />
      <CardContainer>
        <PageHeader title={product.name}>
          <UpdateProductSelector
            product={product}
            type="basic_information"
            drawerTitle="Update product information"
          />
          <DeleteProductSelector product={product} />
        </PageHeader>
        <div css={wrapperStyle}>
          <Divider />
          <CardInformationRow title="Admin name (Only visible for admins)">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
              css={descriptionStyle}
            >
              {product?.name || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Display name (Visible for customers)">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
              css={descriptionStyle}
            >
              {product?.title || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Product category">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
              css={descriptionStyle}
            >
              {product?.categoryName || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Description">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
              css={descriptionStyle}
            >
              {product?.description || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Country">
            <StyledText
              className="bodyTextSemibold mb-0"
              color={colors.textPrimary}
            >
              {getCountryInfo(product?.countryCode).name}{" "}
              {getCountryInfo(product?.countryCode).emojiFlag}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Product type" titleVerticallyCentered>
            <SmallBadge
              backgroundColor={
                getColorsForClassificationBadge(product.classification)
                  .backgroundColor
              }
              borderColor={
                getColorsForClassificationBadge(product.classification)
                  .borderColor
              }
            >
              <StyledText
                color={
                  getColorsForClassificationBadge(product.classification)
                    .textColor
                }
                className="bodyTextRegular mb-0"
              >
                {getClassificationDisplayValue(product.classification)}
              </StyledText>
            </SmallBadge>
          </CardInformationRow>
          <CardInformationRow title="Product tags">
            <div css={tagsContainerStyle}>
              {product.tags?.length > 0 ? (
                product.tags?.map((tag) => (
                  <div css={tagStyling} key={tag.id}>
                    <SmallBadge
                      backgroundColor={colors.whiteHover}
                      borderColor={colors.greyBorder}
                    >
                      {tag.title}
                    </SmallBadge>
                  </div>
                ))
              ) : (
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  -
                </StyledText>
              )}
            </div>
          </CardInformationRow>
          {product.voucherTypeId && (
            <CardInformationRow title="Voucher user receives">
              <StyledText
                color={colors.textPrimary}
                className="bodyTextSemibold mb-0"
              >
                {voucherTypesList?.find(
                  (voucher) => voucher.id === product.voucherTypeId
                )?.name || "-"}
              </StyledText>
            </CardInformationRow>
          )}
          {product.productPackId && (
            <CardInformationRow title="Voucher stack user receives">
              <StyledText
                color={colors.textPrimary}
                className="bodyTextSemibold mb-0"
              >
                {punchCards?.find(
                  (punchCard) => punchCard.id === product.productPackId
                )?.name || "-"}
              </StyledText>
            </CardInformationRow>
          )}
          {product.productCoolDownRulesetId && (
            <CardInformationRow title="Product cooldown ruleset ID">
              <StyledText
                color={colors.textPrimary}
                className="bodyTextSemibold mb-0"
              >
                {config.environment === "production"
                  ? cooldownRulesets_PRODUCTION.find(
                      (ruleset) =>
                        ruleset.value === product.productCoolDownRulesetId
                    )?.label
                  : cooldownRulesets_STAGING.find(
                      (ruleset) =>
                        ruleset.value === product.productCoolDownRulesetId
                    )?.label || "-"}
              </StyledText>
            </CardInformationRow>
          )}
        </div>
      </CardContainer>
      <CardContainer>
        <PageHeader title="Product image">
          <UpdateProductSelector
            product={product}
            type="image"
            drawerTitle="Update product image"
          />
        </PageHeader>
        <div css={wrapperStyle}>
          <Divider />
          <CardBody css={additionalCardStyle}>
            {product.imageUrl ? (
              <BaseUploadPreview url={product?.imageUrl} />
            ) : (
              <EmptyState
                title="No image uploaded yet"
                subtitle="Add image to this product by pressing 'Edit'"
              />
            )}
          </CardBody>
        </div>
      </CardContainer>
      <CardContainer>
        <PageHeader title="Product pricing">
          <UpdateProductSelector
            product={product}
            type="pricing"
            drawerTitle="Update product pricing"
          />
        </PageHeader>
        <div css={wrapperStyle}>
          <Divider />
          <CardInformationRow title="Product price">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {formatMoneyAmount(product.priceAmount, product.priceCurrency)}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Product delivery price">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {formatMoneyAmount(
                product.priceDeliveryAmount as number,
                product.priceDeliveryCurrency
              )}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Currency">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product.priceDeliveryCurrency}
            </StyledText>
          </CardInformationRow>
          <ProductVatNotice product={product} />
          <CardInformationRow
            title={product.vatRuleId ? VAT_RULE_RATE_LABEL : "VAT Percentage"}
          >
            <StyledText
              className="bodyTextSemibold mb-0"
              color={colors.textPrimary}
            >
              {product.vat}%
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Product price description">
            <StyledText
              className="bodyTextSemibold mb-0"
              color={colors.textPrimary}
            >
              {product.priceDescription || "-"}
            </StyledText>
          </CardInformationRow>
        </div>
      </CardContainer>
      {product.classification === ProductClassification.Generic && (
        <CardContainer>
          <PageHeader title="Addition groups">
            <UpdateProductSelector
              product={product}
              type="addition_groups"
              drawerTitle="Update addition groups"
            />
          </PageHeader>
          {(product.additionGroups || []).length > 0 ? (
            <AdditionGroupsTable
              additionGroups={product.additionGroups || []}
              product={product}
            />
          ) : (
            <div css={wrapperStyle}>
              <Divider />
              <CardBody css={additionalCardStyle}>
                <EmptyState
                  title="No addition groups added yet"
                  subtitle="Add your first addition group to this product by pressing 'Edit'"
                />
              </CardBody>
            </div>
          )}
        </CardContainer>
      )}
      <CardContainer>
        <PageHeader title="Packing items">
          <AddPackingItemsSelector product={product} />
        </PageHeader>
        {product.packagingInstructions &&
        product.packagingInstructions.length > 0 ? (
          <ProductPackingItemsTable product={product} />
        ) : (
          <div css={wrapperStyle}>
            <Divider />
            <CardBody css={additionalCardStyle}>
              <EmptyState
                title="No packing items added yet"
                subtitle="Add your first packing item to this product by pressing 'Add packing item'"
              />
            </CardBody>
          </div>
        )}
      </CardContainer>
      <CardContainer>
        <PageHeader title="Product advanced settings" subtitle="Not required">
          <UpdateProductSelector
            product={product}
            type="advanced_options"
            drawerTitle="Update product advanced settings"
          />
        </PageHeader>
        <div css={wrapperStyle}>
          <Divider />
          <CardInformationRow title="Triggers manual accept of order">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.requiresManualAccept ? "Yes" : "No"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Product eligible for discount">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.isEligibleForDiscount ? "Yes" : "No"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Online POS Product ID">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.onlinePosProductId || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Deliverect Product PLU">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.deliverectProductPlu || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Reference">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.reference || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Kitchen category">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.kitchenCategoryId
                ? kitchenCategories?.find(
                    (cat) => cat.id === product.kitchenCategoryId
                  )?.name
                : "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Sort key">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.sortKey || product.sortKey === 0
                ? product.sortKey
                : "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Product units">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.kitchen_capacity_units ||
              product.kitchen_capacity_units === 0
                ? product.kitchen_capacity_units
                : "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Alcohol percentage">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.alcoholPercentage || product.alcoholPercentage === 0
                ? product.alcoholPercentage
                : "-"}
            </StyledText>
          </CardInformationRow>

          <CardInformationRow title="Weight">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.weight || product?.weight === 0 ? product.weight : "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Length">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.dimensions || product.dimensions?.length === 0
                ? product.dimensions?.length
                : "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Width">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.dimensions || product.dimensions?.width === 0
                ? product.dimensions?.width
                : "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Height">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              {product?.dimensions || product.dimensions?.height === 0
                ? product.dimensions?.height
                : "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Barcodes">
            <div css={tagsContainerStyle}>
              {product.barcodes?.length > 0 ? (
                product.barcodes?.map((barcode, index) => (
                  <div css={tagStyling} key={index}>
                    <SmallBadge
                      backgroundColor={colors.whiteHover}
                      borderColor={colors.greyBorder}
                    >
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        {barcode.value} ({barcode.type})
                      </StyledText>
                    </SmallBadge>
                  </div>
                ))
              ) : (
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  -
                </StyledText>
              )}
            </div>
          </CardInformationRow>
          <CardInformationRow title="Online POS serving group id">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
              css={descriptionStyle}
            >
              {product.servingGroupId || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Simphony reference">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
              css={descriptionStyle}
            >
              {product.simphonyReference || "-"}
            </StyledText>
          </CardInformationRow>
          <CardInformationRow title="Is combo">
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
              css={descriptionStyle}
            >
              {product.isComboMeal ? "Yes" : "No"}
            </StyledText>
          </CardInformationRow>
          {product.classification !== ProductClassification.Addition && (
            <CardInformationRow title="Customer earns points for Challenges and Rewards when purchasing this product">
              <StyledText
                color={colors.textPrimary}
                className="bodyTextSemibold mb-0"
                css={descriptionStyle}
              >
                {product.allowChallengeProgress ? "Yes" : "No"}
              </StyledText>
            </CardInformationRow>
          )}
        </div>
      </CardContainer>
    </PageWrapper>
  );
};

export default ProductUpsert;
